import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import './StashPanel.css';
import { getRarityColor, getRarityClass, getRarityGlow, getRarityLabel } from '../utils/rarityUtils';
import { formatCurrency } from './shared/formatCompact';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '';

// Helper: normalize magic from object {0:{...},1:{...}} to array [{...},{...}]
const normalizeMagic = (magic) => {
  if (!magic) return [];
  if (Array.isArray(magic)) return magic;
  if (typeof magic === 'object') return Object.values(magic);
  return [];
};

const SORT_OPTIONS = [
  { key: 'name',  label: 'Name' },
  { key: 'type',  label: 'Type' },
  { key: 'value', label: 'Value' },
  { key: 'qty',   label: 'Quantity' },
];

// v640: Stash tabs (virtual filters)
const TAB_META = {
  general:     { label: 'General',     icon: '📦', color: '#cccccc' },
  materials:   { label: 'Materials',   icon: '🧬', color: '#a8d958' },
  equipment:   { label: 'Equipment',   icon: '⚔️',  color: '#88c0ff' },
  consumables: { label: 'Consumables', icon: '🧪', color: '#ff9b6e' },
  valuables:   { label: 'Valuables',   icon: '💎', color: '#e6cc80' },
};
const STASH_TAB_ORDER = ['general', 'materials', 'equipment', 'consumables', 'valuables'];

// Classes that can learn spells from tomes
const CASTER_CLASSES = ['Mage', 'Cleric', 'Psion', 'Druid'];

const StashPanel = ({ character, onClose, onCharacterUpdate }) => {
  const canLearnSpells = CASTER_CLASSES.includes(character?.class);
  const [stashData, setStashData] = useState(null);
  const [activeTab, setActiveTab] = useState('stash'); // 'stash' | 'inventory'
  const [message, setMessage] = useState({ text: '', type: 'info' });
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState({});
  // v643.2: first-open tutorial banner — explains permanence + capacity
  const [showTutorial, setShowTutorial] = useState(() => {
    try {
      return !localStorage.getItem(`loria_stash_tutorial_seen_${character?.id || 'default'}`);
    } catch (_) { return false; }
  });
  const dismissTutorial = useCallback(() => {
    setShowTutorial(false);
    try { localStorage.setItem(`loria_stash_tutorial_seen_${character?.id || 'default'}`, '1'); } catch (_) { /* noop */ }
  }, [character?.id]);
  const [playerGold, setPlayerGold] = useState(character?.gold || 0);
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState('type');
  const [stashSubTab, setStashSubTab] = useState('all'); // v640: 'all' | tab id
  const [tooltipItem, setTooltipItem] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef(null);
  const modalRef = useRef(null);
  const tooltipPinned = useRef(false);
  const [actionBusy, setActionBusy] = useState(false);

  // Dismiss tooltip when switching tabs
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setTooltipItem(null);
    tooltipPinned.current = false;
  };

  // Dismiss pinned tooltip when clicking outside item rows
  const handleContentClick = (e) => {
    // If click target is not inside a stash-item-row, dismiss tooltip
    if (tooltipPinned.current && !e.target.closest('.stash-item-row')) {
      tooltipPinned.current = false;
      setTooltipItem(null);
    }
  };

  // After tooltip renders, adjust if it overflows the viewport bottom
  useLayoutEffect(() => {
    const el = tooltipRef.current;
    if (!el || !tooltipItem) return;
    const rect = el.getBoundingClientRect();
    const viewportH = window.innerHeight;
    if (rect.bottom > viewportH - 8) {
      el.style.top = `${Math.max(8, viewportH - rect.height - 8)}px`;
    }
  });

  useEffect(() => {
    loadStash();
  }, []);

  useEffect(() => {
    if (character) setPlayerGold(character.gold || 0);
  }, [character]);

  const loadStash = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/stash`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setStashData(data);
        setPlayerGold(data.gold);
        setMessage({ text: `Inn Storage — ${data.stashUsed}/${data.stashSize} slots used`, type: 'info' });
      } else {
        const err = await response.json().catch(() => ({}));
        setMessage({ text: err.error || 'Failed to open stash', type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'Error connecting to stash: ' + error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeposit = useCallback(async (itemName) => {
    if (actionBusy) return;
    const qty = quantities[`dep_${itemName}`] || 1;
    setActionBusy(true);
    try {
      const token = localStorage.getItem('token');
      // v640: send active sub-tab as deposit target if user has narrowed view
      const body = { itemName, quantity: qty };
      if (stashSubTab && stashSubTab !== 'all') body.tab = stashSubTab;
      const response = await fetch(`${API_BASE_URL}/api/stash/deposit`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (data.success) {
        setStashData(prev => ({ ...prev, stash: data.stash, inventory: data.inventory, stashUsed: data.stashUsed }));
        setPlayerGold(data.gold);
        setMessage({ text: data.message, type: 'success' });
        setQuantities(prev => ({ ...prev, [`dep_${itemName}`]: 1 }));
        if (onCharacterUpdate) onCharacterUpdate();
      } else {
        setMessage({ text: data.error || 'Deposit failed', type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'Error depositing item', type: 'error' });
    } finally {
      setActionBusy(false);
    }
  }, [quantities, onCharacterUpdate, actionBusy, stashSubTab]);

  const handleWithdraw = useCallback(async (itemName) => {
    if (actionBusy) return;
    const qty = quantities[`wd_${itemName}`] || 1;
    setActionBusy(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/stash/withdraw`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemName, quantity: qty })
      });
      const data = await response.json();
      if (data.success) {
        setStashData(prev => ({ ...prev, stash: data.stash, inventory: data.inventory, stashUsed: data.stashUsed }));
        setPlayerGold(data.gold);
        setMessage({ text: data.message, type: 'success' });
        setQuantities(prev => ({ ...prev, [`wd_${itemName}`]: 1 }));
        if (onCharacterUpdate) onCharacterUpdate();
      } else {
        setMessage({ text: data.error || 'Withdraw failed', type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'Error withdrawing item', type: 'error' });
    } finally {
      setActionBusy(false);
    }
  }, [quantities, onCharacterUpdate, actionBusy]);

  const handleUpgrade = useCallback(async () => {
    if (actionBusy) return;
    setActionBusy(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/stash/upgrade`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        setStashData(prev => ({ ...prev, stashSize: data.stashSize, upgradeCost: data.upgradeCost }));
        setPlayerGold(data.gold);
        setMessage({ text: data.message, type: 'success' });
        if (onCharacterUpdate) onCharacterUpdate();
      } else {
        setMessage({ text: data.error || 'Upgrade failed', type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'Error upgrading stash', type: 'error' });
    } finally {
      setActionBusy(false);
    }
  }, [onCharacterUpdate, actionBusy]);

  const handleLearnRecipe = useCallback(async (itemName) => {
    if (actionBusy) return;
    setActionBusy(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/stash/learn-recipe`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemName })
      });
      const data = await response.json();
      if (data.success) {
        setStashData(prev => ({ ...prev, stash: data.stash, stashUsed: data.stashUsed }));
        setPlayerGold(data.gold);
        setMessage({ text: data.message, type: 'success' });
        if (onCharacterUpdate) onCharacterUpdate();
      } else {
        setMessage({ text: data.error || 'Failed to learn recipe', type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'Error learning recipe', type: 'error' });
    } finally {
      setActionBusy(false);
    }
  }, [onCharacterUpdate, actionBusy]);

  const handleQuantityChange = (key, value, max) => {
    const num = parseInt(value) || 1;
    setQuantities(prev => ({ ...prev, [key]: Math.max(1, Math.min(max || 20, num)) }));
  };

  // v640: Move a stash item to a different tab.
  const handleMoveTab = useCallback(async (itemName, fromTab, toTab) => {
    if (actionBusy || !toTab || fromTab === toTab) return;
    setActionBusy(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/stash/move-tab`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemName, fromTab, toTab })
      });
      const data = await response.json();
      if (data.success) {
        setStashData(prev => ({ ...prev, stash: data.stash, stashUsed: data.stashUsed }));
        setMessage({ text: data.message, type: 'success' });
      } else {
        setMessage({ text: data.error || 'Move failed', type: 'error' });
      }
    } catch (err) {
      setMessage({ text: 'Error moving item', type: 'error' });
    } finally {
      setActionBusy(false);
    }
  }, [actionBusy]);

  // v640: Auto-categorize every stash item by inferred tab.
  const handleSortTabs = useCallback(async () => {
    if (actionBusy) return;
    setActionBusy(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/stash/sort-tabs`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        setStashData(prev => ({ ...prev, stash: data.stash, stashUsed: data.stashUsed }));
        setMessage({ text: data.message, type: 'success' });
      } else {
        setMessage({ text: data.error || 'Sort failed', type: 'error' });
      }
    } catch (err) {
      setMessage({ text: 'Error sorting stash', type: 'error' });
    } finally {
      setActionBusy(false);
    }
  }, [actionBusy]);

  const handleMouseEnter = (e, item) => {
    // Don't override a click-pinned tooltip
    if (tooltipPinned.current) return;
    showTooltip(e, item);
  };

  const handleMouseLeave = () => {
    // Don't dismiss if click-pinned
    if (tooltipPinned.current) return;
    setTooltipItem(null);
  };

  const handleItemClick = (e, item) => {
    // Don't trigger tooltip toggle when clicking controls (buttons, inputs)
    if (e.target.closest('.stash-item-controls')) return;
    // Toggle tooltip on click/tap for touch devices
    if (tooltipItem && tooltipItem === item && tooltipPinned.current) {
      // Clicking the same item again — dismiss
      tooltipPinned.current = false;
      setTooltipItem(null);
    } else {
      tooltipPinned.current = true;
      showTooltip(e, item);
    }
  };

  const showTooltip = (e, item) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const modalRect = modalRef.current?.getBoundingClientRect() || { left: 0, width: 600 };
    const tooltipWidth = 240;
    const centeredX = (modalRect.width - tooltipWidth) / 2;
    setTooltipItem(item);
    setTooltipPos({
      x: Math.max(8, Math.min(modalRect.left + centeredX, window.innerWidth - tooltipWidth - 8)),
      y: rect.bottom + 4
    });
  };

  if (loading) {
    return (
      <div className="stash-overlay" onClick={onClose}>
        <div className="stash-modal" onClick={e => e.stopPropagation()}>
          <div className="stash-header"><h2>Loading stash...</h2></div>
          <div className="stash-content"><div className="stash-empty">Loading storage...</div></div>
        </div>
      </div>
    );
  }

  const stashRaw = stashData?.stash || [];
  const inventoryRaw = stashData?.inventory || [];
  const stashSize = stashData?.stashSize || 40;
  const stashUsed = stashData?.stashUsed || 0;
  const upgradeCost = stashData?.upgradeCost || 500;
  const canAffordUpgrade = playerGold >= upgradeCost;

  // Filter and sort items
  const filterAndSort = (items) => {
    let result = [...items];
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      result = result.filter(item =>
        item.name.toLowerCase().includes(q) ||
        (item.description || '').toLowerCase().includes(q) ||
        (item.type || '').toLowerCase().includes(q)
      );
    }
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name':  return a.name.localeCompare(b.name);
        case 'type':  return (a.type || '').localeCompare(b.type || '') || a.name.localeCompare(b.name);
        case 'value': return (b.cost || 0) - (a.cost || 0);
        case 'qty':   return (b.quantity || 1) - (a.quantity || 1);
        default:      return 0;
      }
    });
    return result;
  };

  // v640: filter stash by active sub-tab BEFORE search/sort
  const stashFiltered = stashSubTab === 'all'
    ? stashRaw
    : stashRaw.filter(it => (it.tab || 'general') === stashSubTab);
  const stash = filterAndSort(stashFiltered);
  const inventory = filterAndSort(inventoryRaw);

  // v640: counts per tab for the tab strip
  const tabCounts = { all: stashRaw.length };
  for (const t of STASH_TAB_ORDER) tabCounts[t] = 0;
  for (const it of stashRaw) {
    const t = it.tab || 'general';
    tabCounts[t] = (tabCounts[t] || 0) + 1;
  }

  // Build lookup maps so each tab can show cross-counts
  const stashQtyByName = {};
  for (const item of stashRaw) {
    stashQtyByName[item.name] = (stashQtyByName[item.name] || 0) + (item.quantity || 1);
  }
  const invQtyByName = {};
  for (const item of inventoryRaw) {
    invQtyByName[item.name] = (invQtyByName[item.name] || 0) + (item.quantity || 1);
  }

  return (
    <div className="stash-overlay" onClick={onClose} tabIndex={-1}>
      <div className="stash-modal" ref={modalRef} onClick={e => { e.stopPropagation(); handleContentClick(e); }}>
        {/* Header */}
        <div className="stash-header">
          <h2>📦 Inn Storage</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div className="stash-capacity">
              {stashUsed}/{stashSize} slots
            </div>
            <div className="stash-gold">
              <span className="stash-gold-icon">🪙</span>
              <span>{formatCurrency(playerGold)}</span>
            </div>
            <button className="stash-close-btn" onClick={onClose} title="Close (Esc)">✖</button>
          </div>
        </div>

        {/* v643.2: first-open tutorial banner */}
        {showTutorial && (
          <div
            role="note"
            style={{
              margin: '10px 14px 0',
              padding: '10px 14px',
              background: 'linear-gradient(135deg, rgba(184,157,101,0.18), rgba(60,40,20,0.32))',
              border: '1px solid rgba(184,157,101,0.5)',
              borderRadius: 8,
              color: '#f3e8c8',
              fontSize: '0.86rem',
              lineHeight: 1.45,
              display: 'flex',
              gap: '10px',
              alignItems: 'flex-start',
            }}
          >
            <span style={{ fontSize: '1.4em', lineHeight: 1 }}>📦</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: '#ffd87a', marginBottom: 3 }}>Inn Storage — your safe deposit</div>
              <div>
                Items here are <strong>persistent</strong> across deaths and never lost. Capacity is capped at <strong>{stashSize} slots</strong>; upgrade by progressing the inn quest line. Use the tabs to deposit from your bag or withdraw what you need.
              </div>
            </div>
            <button
              type="button"
              onClick={dismissTutorial}
              title="Got it"
              style={{
                background: 'transparent',
                border: '1px solid rgba(184,157,101,0.6)',
                color: '#ffd87a',
                borderRadius: 4,
                padding: '2px 8px',
                cursor: 'pointer',
                fontSize: '0.85em',
                fontWeight: 600,
              }}
            >
              Got it
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="stash-tabs">
          <button
            className={`stash-tab ${activeTab === 'stash' ? 'active' : ''}`}
            onClick={() => handleTabChange('stash')}
          >
            📦 Stash ({stashUsed})
          </button>
          <button
            className={`stash-tab ${activeTab === 'inventory' ? 'active' : ''}`}
            onClick={() => handleTabChange('inventory')}
          >
            🎒 Inventory ({inventoryRaw.length})
          </button>
          <button
            className={`stash-tab ${activeTab === 'upgrade' ? 'active' : ''}`}
            onClick={() => handleTabChange('upgrade')}
          >
            ⬆️ Upgrade
          </button>
        </div>

        {/* Search & Sort toolbar (only for stash/inventory tabs) */}
        {activeTab !== 'upgrade' && (
          <div className="stash-toolbar">
            <input
              className="stash-search"
              type="text"
              placeholder="🔍 Search items..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <select
              className="stash-sort"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              {SORT_OPTIONS.map(opt => (
                <option key={opt.key} value={opt.key}>Sort: {opt.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* Content */}
        <div className="stash-content">
          {activeTab === 'stash' ? (
            <>
              {/* v640: Stash sub-tab strip */}
              <div className="stash-subtab-strip" style={{ display: 'flex', gap: 4, padding: '6px 10px', flexWrap: 'wrap', borderBottom: '1px solid #2a2a3a', alignItems: 'center' }}>
                <button
                  className={`stash-subtab ${stashSubTab === 'all' ? 'active' : ''}`}
                  onClick={() => setStashSubTab('all')}
                  style={{
                    padding: '3px 10px', fontSize: '0.85em', cursor: 'pointer',
                    background: stashSubTab === 'all' ? '#3a3a5a' : '#1a1a2a',
                    color: '#fff', border: '1px solid #444', borderRadius: 4
                  }}
                >All ({tabCounts.all})</button>
                {STASH_TAB_ORDER.map(t => (
                  <button
                    key={t}
                    className={`stash-subtab ${stashSubTab === t ? 'active' : ''}`}
                    onClick={() => setStashSubTab(t)}
                    title={`${TAB_META[t].label} — ${tabCounts[t]} items`}
                    style={{
                      padding: '3px 10px', fontSize: '0.85em', cursor: 'pointer',
                      background: stashSubTab === t ? '#3a3a5a' : '#1a1a2a',
                      color: TAB_META[t].color, border: '1px solid #444', borderRadius: 4,
                      opacity: tabCounts[t] === 0 && stashSubTab !== t ? 0.45 : 1
                    }}
                  >{TAB_META[t].icon} {TAB_META[t].label} ({tabCounts[t]})</button>
                ))}
                <button
                  onClick={handleSortTabs}
                  disabled={actionBusy}
                  title="Auto-categorize every stash item by type"
                  style={{
                    marginLeft: 'auto', padding: '3px 10px', fontSize: '0.85em', cursor: 'pointer',
                    background: '#2a3a1a', color: '#a8d958', border: '1px solid #5a8a3a', borderRadius: 4
                  }}
                >🗂 Auto-Sort</button>
              </div>
              {stash.length > 0 ? (
                stash.map((item, idx) => {
                const available = item.quantity || 1;
                const qty = quantities[`wd_${item.name}`] || 1;
                const inInv = invQtyByName[item.name] || 0;
                const itemTab = item.tab || 'general';
                return (
                  <div key={idx} className="stash-item-row" onMouseEnter={(e) => handleMouseEnter(e, item)} onMouseLeave={handleMouseLeave} onClick={(e) => handleItemClick(e, item)}>
                    <div className="stash-item-info">
                      <div className={`stash-item-name ${getRarityClass(item)}`} style={{ color: getRarityColor(item), textShadow: getRarityGlow(item) }}>
                        {item.name}
                        {available > 1 && <span className="stash-item-qty-badge">x{available}</span>}
                        {stashSubTab === 'all' && (
                          <span className="stash-tab-badge" title={`Tab: ${TAB_META[itemTab].label}`} style={{ marginLeft: 6, fontSize: '0.75em', color: TAB_META[itemTab].color, opacity: 0.85 }}>
                            {TAB_META[itemTab].icon}
                          </span>
                        )}
                        {inInv > 0 && <span className="stash-cross-count inv" title={`${inInv} in inventory`}>🎒{inInv}</span>}
                      </div>
                      <div className="stash-item-desc">{(item.description && item.description !== 'Unknown item') ? item.description : (item.type || '')}</div>
                    </div>
                    <div className="stash-item-controls">
                      {item.type === 'recipe_scroll' && (
                        <button className="stash-learn-btn" onClick={() => handleLearnRecipe(item.name)} title="Learn this recipe">
                          📜 Learn
                        </button>
                      )}
                      <select
                        className="stash-move-tab"
                        value={itemTab}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => { const v = e.target.value; if (v !== itemTab) handleMoveTab(item.name, itemTab, v); }}
                        disabled={actionBusy}
                        title="Move to tab"
                        style={{ fontSize: '0.8em', padding: '2px 4px', background: '#1a1a2a', color: '#ccc', border: '1px solid #444', borderRadius: 3 }}
                      >
                        {STASH_TAB_ORDER.map(t => (
                          <option key={t} value={t}>{TAB_META[t].icon} {TAB_META[t].label}</option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min="1"
                        max={available}
                        value={Math.min(qty, available)}
                        onChange={e => handleQuantityChange(`wd_${item.name}`, e.target.value, available)}
                        className="stash-qty-input"
                      />
                      <button className="stash-withdraw-btn" onClick={() => handleWithdraw(item.name)}>
                        ← Take
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="stash-empty">{stashSubTab === 'all' ? 'Your stash is empty. Switch to the Inventory tab to deposit items.' : `No items in ${TAB_META[stashSubTab].label} tab.`}</div>
            )}
            </>
          ) : activeTab === 'inventory' ? (
            inventory.length > 0 ? (
              inventory.map((item, idx) => {
                const available = item.quantity || 1;
                const qty = quantities[`dep_${item.name}`] || 1;
                const inStash = stashQtyByName[item.name] || 0;
                return (
                  <div key={idx} className="stash-item-row" onMouseEnter={(e) => handleMouseEnter(e, item)} onMouseLeave={handleMouseLeave} onClick={(e) => handleItemClick(e, item)}>
                    <div className="stash-item-info">
                      <div className={`stash-item-name ${getRarityClass(item)}`} style={{ color: getRarityColor(item), textShadow: getRarityGlow(item) }}>
                        {item.name}
                        {available > 1 && <span className="stash-item-qty-badge">x{available}</span>}
                        {inStash > 0 && <span className="stash-cross-count" title={`${inStash} already in stash`}>📦{inStash}</span>}
                      </div>
                      <div className="stash-item-desc">{(item.description && item.description !== 'Unknown item') ? item.description : (item.type || '')}</div>
                    </div>
                    <div className="stash-item-controls">
                      <input
                        type="number"
                        min="1"
                        max={available}
                        value={Math.min(qty, available)}
                        onChange={e => handleQuantityChange(`dep_${item.name}`, e.target.value, available)}
                        className="stash-qty-input"
                      />
                      <button className="stash-deposit-btn" onClick={() => handleDeposit(item.name)}>
                        Store →
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="stash-empty">Your inventory is empty.</div>
            )
          ) : (
            /* Upgrade Tab */
            <div className="stash-upgrade-panel">
              <div className="stash-upgrade-icon">🏗️</div>
              <h3>Expand Your Storage</h3>
              <p className="stash-upgrade-desc">
                The innkeeper can set aside more room for your belongings — for a fee.
              </p>
              <div className="stash-upgrade-info">
                <div className="stash-upgrade-stat">
                  <span>Current Size:</span>
                  <span className="stash-upgrade-value">{stashSize} slots</span>
                </div>
                <div className="stash-upgrade-stat">
                  <span>After Upgrade:</span>
                  <span className="stash-upgrade-value">{stashSize + 10} slots</span>
                </div>
                <div className="stash-upgrade-stat">
                  <span>Cost:</span>
                  <span className={`stash-upgrade-cost ${canAffordUpgrade ? '' : 'too-expensive'}`}>
                    🪙 {formatCurrency(upgradeCost)} gold
                  </span>
                </div>
              </div>
              <button
                className="stash-upgrade-btn"
                disabled={!canAffordUpgrade}
                onClick={handleUpgrade}
                title={canAffordUpgrade ? `Upgrade for ${upgradeCost} gold` : 'Not enough gold'}
              >
                {canAffordUpgrade ? `⬆️ Upgrade (+10 slots) — ${formatCurrency(upgradeCost)} gp` : `Not enough gold (need ${formatCurrency(upgradeCost)})`}
              </button>
            </div>
          )}
        </div>

        {/* Message */}
        <div className={`stash-message ${message.type}`}>
          {message.text}
        </div>
      </div>

      {/* Item Tooltip - rendered outside modal to avoid overflow:hidden clipping */}
      {tooltipItem && (
        <div
          className="stash-tooltip"
          ref={tooltipRef}
          style={{ position: 'fixed', left: tooltipPos.x, top: tooltipPos.y, zIndex: 10001 }}
        >
          <div className={`stash-tooltip-name ${getRarityClass(tooltipItem)}`} style={{ color: getRarityColor(tooltipItem), textShadow: getRarityGlow(tooltipItem) }}>{tooltipItem.name}</div>
          <div className="stash-tooltip-rarity" style={{ color: getRarityColor(tooltipItem) }}>{getRarityLabel(tooltipItem)}</div>
          <div className="stash-tooltip-type">{(tooltipItem.type || '').toUpperCase()}</div>
          {tooltipItem.description && tooltipItem.description !== 'Unknown item' && <div className="stash-tooltip-desc">{tooltipItem.description}</div>}
          {tooltipItem.lore && <div className="stash-tooltip-lore">{tooltipItem.lore}</div>}
          <div className="stash-tooltip-divider" />

          {tooltipItem.quality && tooltipItem.quality !== 'standard' && (
            <div className="stash-tooltip-stat" style={{ color: '#d4a017' }}>Quality: {tooltipItem.quality}</div>
          )}
          {tooltipItem.enhancement > 0 && (
            <div className="stash-tooltip-stat" style={{ color: '#4caf50' }}>Enhanced: +{tooltipItem.enhancement}</div>
          )}

          {tooltipItem.damage && <div className="stash-tooltip-stat">Damage: {tooltipItem.damage}</div>}
          {tooltipItem.att > 0 && <div className="stash-tooltip-stat">Attack: +{tooltipItem.att}</div>}
          {tooltipItem.bonus > 0 && <div className="stash-tooltip-stat">Defense: +{tooltipItem.bonus}</div>}
          {tooltipItem.dr > 0 && <div className="stash-tooltip-stat">Dmg Reduction: {tooltipItem.dr}</div>}
          {tooltipItem.mtype && <div className="stash-tooltip-stat">Melee Type: {tooltipItem.mtype}</div>}
          {tooltipItem.size && <div className="stash-tooltip-stat" style={{ color: '#d4a017' }}>Capacity: +{tooltipItem.size} slots</div>}

          {tooltipItem.mdamage && tooltipItem.mtype && (
            <div className="stash-tooltip-stat" style={{ color: '#ff9800' }}>{tooltipItem.mtype} damage: {tooltipItem.mdamage}</div>
          )}

          {tooltipItem.enemy && (
            <div className="stash-tooltip-stat" style={{ color: '#e74c3c' }}>Bane: {tooltipItem.enemy}</div>
          )}

          {tooltipItem.sockets && tooltipItem.sockets.length > 0 && (
            <div className="stash-tooltip-stat" style={{ color: '#e91e63' }}>
              Gems: {tooltipItem.sockets.filter(s => s).length}/{tooltipItem.sockets.length} socketed
            </div>
          )}

          {tooltipItem.enchantments && tooltipItem.enchantments.length > 0 && (
            <div className="stash-tooltip-magic">
              {tooltipItem.enchantments.map((ench, i) => (
                <div key={i} className="stash-tooltip-stat" style={{ color: '#ffb74d' }}>📜 {ench.label}</div>
              ))}
            </div>
          )}

          {tooltipItem.hp > 0 && <div className="stash-tooltip-stat" style={{ color: '#e74c3c' }}>Restores {tooltipItem.hp} HP</div>}
          {tooltipItem.mp > 0 && <div className="stash-tooltip-stat" style={{ color: '#3498db' }}>Restores {tooltipItem.mp} MP</div>}
          {tooltipItem.spell && <div className="stash-tooltip-stat" style={{ color: '#f39c12' }}>Teaches: {tooltipItem.spell}</div>}
          {tooltipItem.type === 'tome' && canLearnSpells === false && (
            <div className="stash-tooltip-stat" style={{ color: '#e74c3c', fontStyle: 'italic' }}>⚠ Your class cannot learn magic</div>
          )}

          {tooltipItem.magic && normalizeMagic(tooltipItem.magic).length > 0 && (
            <div className="stash-tooltip-magic">
              {normalizeMagic(tooltipItem.magic).map((m, i) => (
                <div key={i} className="stash-tooltip-stat" style={{ color: '#bf7fff' }}>
                  +{m.mod || m.bonus || 0} {m.type === 'ablup' ? (m.abl || '').toUpperCase() : m.type.toUpperCase()}
                </div>
              ))}
            </div>
          )}

          {tooltipItem.level > 1 && <div className="stash-tooltip-level">Requires Lv {tooltipItem.level}</div>}

          {tooltipItem.type === 'shield' && (
            <div className="stash-tooltip-stat" style={{ color: '#ff9800', fontStyle: 'italic' }}>⚠ Non-Fighter classes: -3 ATT penalty</div>
          )}

          <div className="stash-tooltip-value">{tooltipItem.cost || 0} gp (sell: {Math.floor((tooltipItem.cost || 0) / 4)} gp)</div>
          {(tooltipItem.quantity || 1) > 1 && <div className="stash-tooltip-qty">Quantity: {tooltipItem.quantity}</div>}
        </div>
      )}
    </div>
  );
};

export default StashPanel;
